import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { QuestionConceptRef, QuestionListItem, QuestionPurpose, QuestionTaxonomyTag } from '@course-authoring/shared-types';
import { ModalShell } from '../ModalShell';
import { useAdminNarrowViewport } from '../../admin-ui';
import {
  isAuthorWithAiQuestionType,
  type AuthorWithAiQuestionType,
  type BuildQuestionFilters,
} from '../../api/authorWithAiApi';
import { useBuildQuestionFacets, useBuildQuestionPage } from '../../api/authorWithAiQueries';
import type { LibraryConcept } from '../../api/centerstageApi';
import type { AllowedPageSize } from '../table/ListPagination';
import { usePageBoundsReset } from '../../hooks/useListControls';
import { CenterstageConceptsModal, type ConceptsModalCopy } from '../centerstage/CenterstageConceptsModal';
import type { CenterstageFilterChip } from '../centerstage/CenterstageFilterChips';
import { QuestionBankCards } from '../questionBankBrowse/QuestionBankCards';
import { QuestionBankDetail } from '../questionBankBrowse/QuestionBankDetail';
import { QuestionBankFindRow } from '../questionBankBrowse/QuestionBankFindRow';
import { indexTaxonomyLabels, orderLabelIdsForChips } from '../questionBankBrowse/questionBankLabels';
import { QuestionBankSortRow, sortOptionFor } from '../questionBankBrowse/QuestionBankSortRow';
import { QuestionBankTaxonomyPopover } from '../questionBankBrowse/QuestionBankTaxonomyPopover';
import { groupKey } from '../questionBankBrowse/QuestionBankTaxonomySections';
import { QUESTION_BANK_BROWSE_COPY as COPY } from '../questionBankBrowse/questionBankBrowseCopy';
import { createQuestionBankBrowseStyles, type QuestionBankBrowseStyles } from '../questionBankBrowse/questionBankBrowseStyles';
import { PICKER_COPY } from './authorWithAiCopy';

/**
 * The Author with AI question picker (Sara's "Choose a question — in-flow
 * picker", 2026-11-03): the Question Bank page in a dialog. Search with the
 * concept autosuggest and browse-all, Type and Purpose that are never
 * empty, the "Skills & risk areas" pill whose popover holds the page's
 * framework sections (Sentiment only), four sorts, cards, pages, and a
 * detail dialog. In single mode (the anchor question) the detail ends in
 * "Use this one". In multi mode ("Choose questions" at the pairing step,
 * Sara's walkthrough 2026-11-04, replacing the old five-row list) the detail
 * selects or unselects the question, selected cards carry a check, and a
 * footer counts them and ends in "Use N questions".
 */

/** What each type can produce (author-api's purpose rules), first entry the default. */
export const PURPOSES_BY_TYPE: Readonly<Record<AuthorWithAiQuestionType, readonly QuestionPurpose[]>> = {
  'likert-scale': ['Sentiment', 'Reflection'],
  'single-choice-answer': ['Knowledge', 'Reflection'],
  'workplace-color-spectrum': ['Knowledge'],
  categorization: ['Knowledge'],
};

/** The prototype's dialog height, min(840px, 100%): fixed, so the frame does not change as the list does. */
const PICKER_HEIGHT = 'min(840px, calc(100vh - 56px))';

const OPENING = {
  blockType: 'likert-scale' as AuthorWithAiQuestionType,
  purpose: 'Sentiment' as QuestionPurpose,
  sortKey: 'responses',
  pageSize: 25 as AllowedPageSize,
};

const CONCEPTS_COPY: ConceptsModalCopy = {
  title: COPY.conceptsTitle,
  countsLoading: COPY.countsLoading,
  countsError: COPY.countsError,
  noItems: COPY.noQuestionsForConcept,
};

interface UseThisOneFooterProps {
  styles: QuestionBankBrowseStyles['browse'];
  item: QuestionListItem;
  error?: string;
  busy: boolean;
  /** Multi mode: whether the question is already selected; undefined in single mode. */
  selected?: boolean;
  onClose: () => void;
  /** Single mode: use the question. Multi mode: select or unselect it. */
  onUse: (questionDefinitionId: number) => void;
}

/** The picker's detail footer: the server's rejection, Close and "Use this one" (single) or "Select this one" / "Unselect" (multi). */
function UseThisOneFooter({ styles, item, error, busy, selected, onClose, onUse }: UseThisOneFooterProps) {
  const useButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasBusy = useRef(false);
  // "Use this one" is disabled while the pick is in flight, which drops focus; when the pick
  // settles with the dialog still open (a rejection), take focus back so Escape keeps working.
  useEffect(() => {
    if (wasBusy.current && !busy) {
      const dialog = useButtonRef.current?.closest('[role="dialog"]');
      if (dialog && !dialog.contains(document.activeElement)) useButtonRef.current?.focus();
    }
    wasBusy.current = busy;
  }, [busy]);
  return (
    <>
      {error ? <p role="alert" style={styles.footerError}>{error}</p> : null}
      <button type="button" style={styles.secondaryButton} onClick={onClose}>{COPY.close}</button>
      <button
        ref={useButtonRef}
        type="button"
        style={{ ...styles.primaryButton, ...(busy ? styles.buttonDisabled : {}) }}
        disabled={busy}
        onClick={() => onUse(item.questionDefinitionId)}
      >
        {selected === undefined ? COPY.useThisOne : selected ? COPY.unselect : COPY.selectThisOne}
      </button>
    </>
  );
}

export interface QuestionBankPickerProps {
  buildId: string;
  isOpen: boolean;
  /** Single (the anchor question, one pick from the detail) or multi (the pairing step: select several, then "Use N questions"). */
  mode?: 'single' | 'multi';
  onClose: () => void;
  /** The picked question ids: one in single mode, the selection in multi mode. */
  onPick: (libraryIds: string[]) => void;
  /** Server rejections keyed by question id, shown in that question's detail dialog and, in multi mode, in the footer. */
  rowErrors?: Readonly<Record<string, string>>;
  busy?: boolean;
}

export function QuestionBankPicker({ buildId, isOpen, mode = 'single', onClose, onPick, rowErrors = {}, busy = false }: QuestionBankPickerProps) {
  const narrow = useAdminNarrowViewport();
  const styles = useMemo(() => createQuestionBankBrowseStyles({ narrow }), [narrow]);
  const p = styles.browse;
  const titleId = useId();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  // Bumped when the control that had focus goes away: a removed chip, "Show all questions", or the detail
  // handing focus back to a card that is disabled while a label filter reloads the list.
  const [focusSearch, setFocusSearch] = useState(0);
  const takeFocus = () => setFocusSearch((tick) => tick + 1);

  const [draft, setDraft] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [conceptId, setConceptId] = useState<number | null>(null);
  /** Checked framework labels, in the order they were checked. */
  const [labelIds, setLabelIds] = useState<readonly number[]>([]);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set());
  const [blockType, setBlockType] = useState<AuthorWithAiQuestionType>(OPENING.blockType);
  const [purpose, setPurpose] = useState<QuestionPurpose>(OPENING.purpose);
  const [sortKey, setSortKey] = useState<string>(OPENING.sortKey);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<AllowedPageSize>(OPENING.pageSize);
  const [conceptsOpen, setConceptsOpen] = useState(false);
  const [detail, setDetail] = useState<QuestionListItem | null>(null);
  /** Multi mode: the questions selected so far, in selection order. */
  const [selectedIds, setSelectedIds] = useState<readonly number[]>([]);

  // Closing discards everything, so the picker reopens in the opening state.
  useEffect(() => {
    if (isOpen) return;
    setSelectedIds([]);
    setDraft('');
    setSearchTerm('');
    setConceptId(null);
    setLabelIds([]);
    setOpenGroups(new Set());
    setBlockType(OPENING.blockType);
    setPurpose(OPENING.purpose);
    setSortKey(OPENING.sortKey);
    setPage(1);
    setPageSize(OPENING.pageSize);
    setConceptsOpen(false);
    setDetail(null);
  }, [isOpen]);

  // Only Sentiment questions carry framework tags: the pill shows, and labels apply, for Sentiment alone.
  const sentiment = purpose === 'Sentiment';
  const sortOption = sortOptionFor(sortKey);
  const filters: BuildQuestionFilters = useMemo(() => ({
    blockType,
    purpose,
    sort: sortOption.sort,
    order: sortOption.order,
    page,
    pageSize,
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(conceptId !== null ? { conceptId } : {}),
    ...(sentiment && labelIds.length > 0 ? { taxonomyLabelIds: labelIds } : {}),
  }), [blockType, purpose, sortOption, page, pageSize, searchTerm, conceptId, sentiment, labelIds]);
  const list = useBuildQuestionPage(buildId, filters, { enabled: isOpen });
  // A result set that shrinks under the current page lands on its last page, not on an empty one.
  usePageBoundsReset({ page, pageSize, totalCount: list.data?.totalCount ?? 0, isPlaceholderData: list.isPlaceholderData, setPage });
  const facets = useBuildQuestionFacets(buildId, { enabled: isOpen });
  const facetsSettled = facets.isSuccess || facets.isError;

  const concepts = useMemo(() => facets.data?.concepts ?? [], [facets.data]);
  const categories = useMemo(() => facets.data?.categories ?? [], [facets.data]);
  const categoryNames = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  const conceptOptions: LibraryConcept[] = useMemo(
    () => concepts.map((concept) => ({ id: concept.id, name: concept.name, categoryId: concept.categoryId })),
    [concepts],
  );
  // The autosuggest and the browse modal offer only concepts that lead somewhere (PO, 2026-11-03: hide, do not grey out).
  const suggestable = useMemo(() => conceptOptions.filter((concept) => (concepts.find((row) => row.id === concept.id)?.questionCount ?? 0) > 0), [conceptOptions, concepts]);
  const counts = useMemo(() => Object.fromEntries(concepts.map((concept) => [concept.id, concept.questionCount])), [concepts]);
  const conceptName = conceptId !== null ? concepts.find((concept) => concept.id === conceptId)?.name ?? `Concept ${conceptId}` : null;
  const taxonomies = useMemo(() => facets.data?.taxonomies ?? [], [facets.data]);
  const labels = useMemo(() => indexTaxonomyLabels(taxonomies), [taxonomies]);

  const firstPage = () => {
    setPage(1);
    bodyRef.current?.scrollTo?.({ top: 0 });
  };
  const clearLabels = () => {
    setLabelIds([]);
    setOpenGroups(new Set());
  };
  const pickConcept = (id: number) => {
    setDraft('');
    setSearchTerm('');
    setConceptId(id);
    setConceptsOpen(false);
    firstPage();
  };
  const commitSearch = (term: string) => {
    setSearchTerm(term);
    setDraft(term);
    setConceptId(null);
    firstPage();
  };
  // Away from Sentiment no label applies, so the checked ones are dropped and the groups close.
  const changeType = (next: AuthorWithAiQuestionType) => {
    const nextPurpose = PURPOSES_BY_TYPE[next][0]!;
    setBlockType(next);
    setPurpose(nextPurpose);
    if (nextPurpose !== 'Sentiment') clearLabels();
    firstPage();
  };
  const changePurpose = (next: QuestionPurpose) => {
    setPurpose(next);
    if (next !== 'Sentiment') clearLabels();
    firstPage();
  };
  const toggleLabel = (labelId: number) => {
    setLabelIds((current) => (current.includes(labelId) ? current.filter((id) => id !== labelId) : [...current, labelId]));
    firstPage();
  };
  const setGroups = (keys: readonly string[], open: boolean) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (open) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };
  const showAll = () => {
    setDraft('');
    setSearchTerm('');
    setConceptId(null);
    clearLabels();
    setBlockType(OPENING.blockType);
    setPurpose(OPENING.purpose);
    firstPage();
    takeFocus();
  };
  // The detail's label link: that label alone, its parent group open in the popover.
  const filterByLabel = (tag: QuestionTaxonomyTag) => {
    setDetail(null);
    setDraft('');
    setSearchTerm('');
    setConceptId(null);
    if (!sentiment) {
      setBlockType('likert-scale');
      setPurpose('Sentiment');
    }
    setLabelIds([tag.labelId]);
    setGroups([groupKey(tag.taxonomyId, tag.parentLabel)], true);
    firstPage();
    takeFocus();
  };
  // The detail's concept link: that concept alone, the search and every label cleared; Type and Purpose stay.
  const filterByConcept = (concept: QuestionConceptRef) => {
    setDetail(null);
    setDraft('');
    setSearchTerm('');
    setLabelIds([]);
    setConceptId(concept.id);
    firstPage();
    takeFocus();
  };
  // Runs after the detail dialog's own focus restore (a child effect), which fails on the now-disabled card.
  useEffect(() => {
    if (focusSearch === 0) return;
    topRef.current?.querySelector<HTMLElement>('input[role="combobox"]')?.focus();
  }, [focusSearch]);

  const chips: CenterstageFilterChip[] = [];
  if (searchTerm) chips.push({ key: 'search', label: `“${searchTerm}”`, onRemove: () => { setSearchTerm(''); setDraft(''); firstPage(); takeFocus(); } });
  if (conceptName) chips.push({ key: 'concept', label: conceptName, onRemove: () => { setConceptId(null); firstPage(); takeFocus(); } });
  // Names come from the facets; until they answer there is nothing to label a chip with.
  if (facetsSettled && sentiment) {
    for (const labelId of orderLabelIdsForChips(labelIds, labels)) {
      chips.push({
        key: `label-${labelId}`,
        label: labels.get(labelId)?.childLabel ?? COPY.labelFallback(labelId),
        onRemove: () => { setLabelIds((current) => current.filter((id) => id !== labelId)); firstPage(); takeFocus(); },
      });
    }
  }

  const multi = mode === 'multi';
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const toggleSelected = (questionDefinitionId: number) => {
    setSelectedIds((current) => (current.includes(questionDefinitionId) ? current.filter((id) => id !== questionDefinitionId) : [...current, questionDefinitionId]));
    setDetail(null);
  };
  // The first rejection among the selected questions, for the footer; each also shows in its own detail.
  const footError = selectedIds.map((id) => rowErrors[String(id)]).find(Boolean);

  const purposes = PURPOSES_BY_TYPE[blockType];
  const total = list.data?.totalCount ?? 0;
  const items = (list.data?.items ?? []).filter((item) => isAuthorWithAiQuestionType(item.blockType));
  const status: 'pending' | 'error' | 'success' = list.isPending ? 'pending' : list.isError && !list.data ? 'error' : 'success';
  const stale = list.isPlaceholderData;

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        ariaLabelledBy={titleId}
        width="1080px"
        height={PICKER_HEIGHT}
        maxHeight={PICKER_HEIGHT}
        centerOnContent
        presentation="admin"
      >
        <div style={p.header}>
          <div style={p.headerText}>
            <h2 id={titleId} style={p.title}>{COPY.title}</h2>
            <p style={p.description}>{multi ? COPY.pickerDescriptionMulti : COPY.pickerDescription}</p>
          </div>
          <button type="button" style={p.close} onClick={onClose} aria-label={PICKER_COPY.close}>
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '16px' }} />
          </button>
        </div>

        <div ref={topRef} style={p.top}>
          <QuestionBankFindRow
            styles={styles}
            draft={draft}
            onDraftChange={setDraft}
            searchTerm={searchTerm}
            onCommitSearch={commitSearch}
            concepts={suggestable}
            categoryNames={categoryNames}
            onPickConcept={pickConcept}
            onBrowseConcepts={() => setConceptsOpen(true)}
            searchPlaceholder={COPY.pickerSearchPlaceholder}
            blockType={blockType}
            onTypeChange={changeType}
            purposes={purposes}
            purpose={purpose}
            onPurposeChange={changePurpose}
            taxonomyFilter={sentiment ? (
              <QuestionBankTaxonomyPopover
                styles={styles}
                taxonomies={taxonomies}
                checkedLabelIds={labelIds}
                openGroups={openGroups}
                onSetGroups={setGroups}
                onToggleLabel={toggleLabel}
              />
            ) : null}
          />
          {facets.isError ? <p role="status" style={p.note}>{COPY.facetsError}</p> : null}
          <QuestionBankSortRow
            styles={styles}
            variant="picker"
            sortKey={sortKey}
            onSortChange={(value) => { setSortKey(value); firstPage(); }}
            chips={chips}
            total={total}
            page={page}
            pageSize={pageSize}
            onShowAll={showAll}
          />
        </div>

        <div ref={bodyRef} style={p.body}>
          <QuestionBankCards
            styles={styles}
            status={status}
            items={items}
            sortKey={sortKey}
            stale={stale}
            glyph="always"
            total={total}
            page={page}
            pageSize={pageSize}
            onOpen={setDetail}
            onRetry={() => void list.refetch()}
            onPageChange={(next) => { setPage(next); bodyRef.current?.scrollTo?.({ top: 0 }); }}
            onPageSizeChange={(next) => { setPageSize(next); firstPage(); }}
            selectedIds={multi ? selectedSet : undefined}
          />
        </div>
        {multi ? (
          <div style={p.pickerFoot}>
            {footError
              ? <p role="alert" style={p.footerError}>{footError}</p>
              : <p style={p.footCount}>{selectedIds.length > 0 ? COPY.checkedCount(selectedIds.length) : COPY.nothingSelected}</p>}
            <button type="button" style={p.secondaryButton} onClick={onClose}>{COPY.close}</button>
            <button
              type="button"
              style={{ ...p.primaryButton, ...(selectedIds.length === 0 || busy ? p.buttonDisabled : {}) }}
              disabled={selectedIds.length === 0 || busy}
              onClick={() => onPick(selectedIds.map(String))}
            >
              {PICKER_COPY.confirmQuestions(selectedIds.length)}
            </button>
          </div>
        ) : null}
      </ModalShell>

      {/* Siblings of the picker, not children, so Escape and a backdrop click close only the innermost layer. */}
      <CenterstageConceptsModal
        styles={styles.centerstage}
        isOpen={isOpen && conceptsOpen}
        categories={categories}
        concepts={suggestable}
        counts={facets.data ? counts : undefined}
        countsStatus={facets.isError ? 'error' : facets.data ? 'success' : 'pending'}
        onPick={pickConcept}
        onClose={() => setConceptsOpen(false)}
        copy={CONCEPTS_COPY}
      />
      <QuestionBankDetail
        styles={styles}
        item={isOpen ? detail : null}
        width="680px"
        footer={(item) => (
          <UseThisOneFooter
            styles={p}
            item={item}
            error={rowErrors[String(item.questionDefinitionId)]}
            busy={busy}
            selected={multi ? selectedSet.has(item.questionDefinitionId) : undefined}
            onClose={() => setDetail(null)}
            onUse={(questionDefinitionId) => (multi ? toggleSelected(questionDefinitionId) : onPick([String(questionDefinitionId)]))}
          />
        )}
        onClose={() => setDetail(null)}
        onFilterLabel={filterByLabel}
        onFilterConcept={filterByConcept}
      />
    </>
  );
}
